/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The overflow menu — the component that lets a screen have ONE primary action.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * Recipes offered five equal top-level buttons; a recipe offered six above its
 * own title; Plan offered three in its header. When every capability is a
 * button of the same weight, the interface has declined to say which one
 * matters, and the user has to read all of them to find the one they wanted.
 *
 * This is where the other five go. It is not a place to hide things — the
 * trigger is always visible and always keyboard-reachable — it is a place to
 * rank them.
 *
 * ── TOUCH ───────────────────────────────────────────────────────────────────
 * Closes on outside pointerdown, Escape, and on scroll of any ancestor. That
 * last one matters on a phone: an absolutely-positioned menu that stays put
 * while the list behind it scrolls looks like a rendering bug.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { Icon, type IconName } from "./Icon.tsx";

export interface ActionItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  /** Renders in danger tone, below a separator. */
  destructive?: boolean;
  disabled?: boolean;
  /** Small grey line under the label — for consequences worth stating. */
  hint?: string;
}

export function ActionMenu({
  items, label = "More actions", trigger, align = "right",
}: {
  items: ActionItem[];
  label?: string;
  /** Defaults to a ⋯ icon button. Pass text for a labelled menu like "Week tools". */
  trigger?: ReactNode;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); }
    };
    // `true` — capture phase, so a scroll on any ancestor closes it, not just
    // one that happens to bubble.
    const onScroll = () => setOpen(false);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const enabled = items.filter((i) => !i.disabled);
  if (enabled.length === 0) return null;

  const ordinary = items.filter((i) => !i.destructive);
  const destructive = items.filter((i) => i.destructive);

  const row = (item: ActionItem, i: number) => (
    <button
      key={`${item.label}-${i}`}
      type="button"
      role="menuitem"
      disabled={item.disabled}
      onClick={() => { setOpen(false); item.onSelect(); }}
      className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left text-sm transition
                  disabled:opacity-40 ${
        item.destructive
          ? "text-danger hover:bg-danger/10"
          : "text-ink hover:bg-paper-3"}`}
    >
      {item.icon && <Icon name={item.icon} className="mt-0.5 h-4 w-4 shrink-0" />}
      <span className="min-w-0">
        <span className="block leading-snug">{item.label}</span>
        {item.hint && <span className="block text-xs text-ink-3">{item.hint}</span>}
      </span>
    </button>
  );

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={trigger ? undefined : label}
        title={trigger ? undefined : label}
        onClick={() => setOpen((o) => !o)}
        className={trigger
          ? "btn-ghost"
          : "inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink-3 transition hover:bg-paper-3 hover:text-ink"}
      >
        {trigger ?? <Icon name="more" className="h-[18px] w-[18px]" />}
        {trigger && <Icon name="chevronDown" className="h-3.5 w-3.5 opacity-60" />}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className={`card absolute z-30 mt-1 min-w-[13rem] max-w-[17rem] overflow-hidden p-1 shadow-soft ${
            align === "right" ? "right-0" : "left-0"}`}
        >
          {ordinary.map(row)}
          {destructive.length > 0 && ordinary.length > 0 && (
            <div className="my-1 border-t border-rule-soft" />
          )}
          {destructive.map(row)}
        </div>
      )}
    </div>
  );
}
